/**
 * Loading Button Component
 *
 * A button component with loading state using the KRP Design System.
 * This is a wrapper around the base Button component for backward compatibility.
 *
 * @deprecated Prefer using Button component directly with loading prop
 */

import React, { ButtonHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

interface LoadingButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  loadingText?: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  loadingIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  loadingText,
  variant = 'primary',
  size = 'md',
  icon,
  loadingIcon,
  fullWidth = false,
  children,
  disabled,
  className = '',
  ...props
}) => {
  // Base classes using Design System
  const baseClasses = cn(
    'inline-flex items-center justify-center font-medium rounded-md',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'transition-all duration-150',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  );

  // Variant classes using Design System colors
  const variantClasses = {
    primary: cn(
      'bg-primary-600 text-white',
      'hover:bg-primary-700 active:bg-primary-800',
      'focus:ring-primary-500',
      'shadow-button hover:shadow-button-hover'
    ),
    secondary: cn(
      'bg-secondary-100 text-secondary-700',
      'border border-secondary-200',
      'hover:bg-secondary-200 active:bg-secondary-300',
      'focus:ring-secondary-400'
    ),
    danger: cn(
      'bg-danger-600 text-white',
      'hover:bg-danger-700 active:bg-danger-800',
      'focus:ring-danger-500',
      'shadow-button hover:shadow-button-hover'
    ),
    success: cn(
      'bg-success-700 text-white',
      'hover:bg-success-800 active:bg-success-800',
      'focus:ring-success-500',
      'shadow-button hover:shadow-button-hover'
    ),
    warning: cn(
      'bg-warning-500 text-white',
      'hover:bg-warning-600 active:bg-warning-700',
      'focus:ring-warning-400',
      'shadow-button hover:shadow-button-hover'
    )
  };

  // Size classes
  const sizeClasses = {
    sm: 'min-h-[44px] sm:min-h-0 h-auto sm:h-8 px-3 text-sm gap-1.5',
    md: 'min-h-[44px] sm:min-h-0 h-auto sm:h-10 px-4 text-sm gap-2',
    lg: 'h-11 px-6 text-base gap-2'
  };

  // Width classes
  const widthClasses = fullWidth ? 'w-full' : '';

  // Loading spinner
  const defaultLoadingIcon = (
    <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  // Determine what to show
  const showIcon = loading ? (loadingIcon || defaultLoadingIcon) : icon;
  const buttonText = loading ? (loadingText || 'กำลังประมวลผล...') : children;
  const isDisabled = disabled || loading;

  return (
    <button
      className={cn(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        widthClasses,
        className
      )}
      disabled={isDisabled}
      {...props}
    >
      {showIcon && (
        <span className="flex items-center flex-shrink-0">
          {showIcon}
        </span>
      )}
      <span>{buttonText}</span>
    </button>
  );
};

// Preset button variants for common use cases
export const SaveButton: React.FC<Omit<LoadingButtonProps, 'variant'>> = (props) => (
  <LoadingButton
    variant="primary"
    loadingText="กำลังบันทึก..."
    {...props}
  >
    {props.children || 'บันทึก'}
  </LoadingButton>
);

export const DeleteButton: React.FC<Omit<LoadingButtonProps, 'variant'>> = (props) => (
  <LoadingButton
    variant="danger"
    loadingText="กำลังลบ..."
    {...props}
  >
    {props.children || 'ลบ'}
  </LoadingButton>
);

export const SubmitButton: React.FC<Omit<LoadingButtonProps, 'variant' | 'type'>> = (props) => (
  <LoadingButton
    variant="primary"
    type="submit"
    loadingText="กำลังส่งข้อมูล..."
    {...props}
  >
    {props.children || 'ส่งข้อมูล'}
  </LoadingButton>
);

export const RefreshButton: React.FC<Omit<LoadingButtonProps, 'variant'>> = (props) => {
  const refreshIcon = (
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );

  return (
    <LoadingButton
      variant="secondary"
      icon={refreshIcon}
      loadingText="กำลังรีเฟรช..."
      {...props}
    >
      {props.children || 'รีเฟรช'}
    </LoadingButton>
  );
};

export const DownloadButton: React.FC<Omit<LoadingButtonProps, 'variant'>> = (props) => {
  const downloadIcon = (
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

  return (
    <LoadingButton
      variant="success"
      icon={downloadIcon}
      loadingText="กำลังดาวน์โหลด..."
      {...props}
    >
      {props.children || 'ดาวน์โหลด'}
    </LoadingButton>
  );
};

export default LoadingButton;